<script lang="ts" setup>
import StoryChapterItem from "@/components/mobile/story/story-chapter/StoryChapterItem.vue";
import MateMediaPreview from "@/components/mobile/overlay/MateMediaPreview.vue";
import { computed, ref } from "vue";
import BoxedTab from "@/components/mobile/common/BoxedTab.vue";
import PlusIcon from "@/components/icon/PlusIcon.vue";
import { useMate } from "@/stores/mate";
import CreateStoryGuide from "@/components/mobile/story/story-chapter/CreateStoryGuide.vue";
import { useRoute, useRouter } from "vue-router";
import StoryChapterPublishPopup from "@/components/mobile/story/story-chapter/StoryChapterPublishPopup.vue";
import i18n from "@/locales";
import { useStory } from "@/stores/story";
import { ScreenshotSharePopupExpose } from "../../popup/ScreenshotSharePopup.vue";
import { onMounted } from "vue";
import { Story, StoryBook } from "@/types/entity";
const props = defineProps<{
  storyBook: StoryBook;
  storyList: Story[];
}>();
const mate = useMate();
const router = useRouter();
const route = useRoute();
const storyStore = useStory();
const screenshotSharePopupRef = ref<ScreenshotSharePopupExpose>();
const isOwner = computed(() => {
  return props.storyBook.mateId === mate.userMate?.id;
});
const showCreateGuide = ref(false);
// filter
const FILTER_ENUM = [
  { text: i18n.global.t("Buttons.all"), value: null },
  { text: i18n.global.t("story.draft"), value: 0 },
  { text: i18n.global.t("story.published"), value: 1 },
];
const selectedFilterIdx = ref(0);
// sorter
const SORTER_ENUM = [
  { text: i18n.global.t("story.new"), value: "desc" },
  { text: i18n.global.t("story.old"), value: "asc" },
];
const selectedSorterIdx = ref(0);
const showStoryCount = ref(3);
const showFullList = ref(isOwner.value);
const sortChapterList = (list: Story[]) => {
  if (SORTER_ENUM[selectedSorterIdx.value].value === "desc") {
    return [...list].sort(
      (a, b) => new Date(a.order!).getTime() - new Date(b.order!).getTime()
    );
  } else {
    return [...list].sort(
      (a, b) => new Date(b.order!).getTime() - new Date(a.order!).getTime()
    );
  }
};
const slicedStoryList = computed(() => {
  if (showFullList.value) return sortChapterList(props.storyList);
  return sortChapterList(props.storyList).slice(0, showStoryCount.value);
});
const filteredChapterList = computed(() => {
  if (FILTER_ENUM[selectedFilterIdx.value].value === null) {
    return slicedStoryList.value;
  }
  return slicedStoryList.value.filter((item) => {
    if (FILTER_ENUM[selectedFilterIdx.value].value === 0) {
      return !item.dtPublish;
    } else {
      return !!item.dtPublish;
    }
  });
});
onMounted(() => {
  if (route.query.showCount)
    showStoryCount.value = Number(route.query.showCount);
});
// gallery-preview
const showPreview = ref(false);
const previewMediaList = ref<Media[]>([]);
const startPosition = ref(0);
// share-popup
const showSharePopup = ref(false);
const toSharePopup = ref<Story | null>(null);
// publish-popup
const toPublishStory = ref<Story | null>(null);
const showPublishPopup = ref(false);
// Lifecycle

// Event Handler
const onPreview = (idx: number, urlList: string[]) => {
  showPreview.value = true;
  previewMediaList.value = urlList.map((url) => {
    return { url } as Media;
  });
  startPosition.value = idx;
};
const onShowThreeMoreClick = () => {
  showStoryCount.value += 3;
  router.replace({
    query: { showCount: showStoryCount.value },
  });
};

const onShareClick = async (
  story: Story,
  isReset?: boolean,
  isVideo?: boolean
) => {
  toSharePopup.value = story;
  const media = await screenshotSharePopupRef.value?.startGenerateScreenshot(
    isReset ? undefined : isVideo ? story.videoMedia : story.screenshotMedia
  );
  if (media) {
    story.screenshotMediaId = media.id;
    story.screenshotMedia = media;
  }
};

const showEditPopup = ref(false);
const currentStory = ref<Story | null>(null);
const onStoryEditSuccess = (story: Story) => {
  showEditPopup.value = false;
};
</script>
<template>
  <div class="p-4">
    <div class="flex justify-between items-center pb-2">
      <div>
        <boxed-tab
          v-if="isOwner && storyList.length > 0"
          v-model:selected-idx="selectedFilterIdx"
        >
          <div v-for="(item, idx) in FILTER_ENUM" :key="idx">
            {{ item.text }}
          </div>
        </boxed-tab>
        <boxed-tab
          v-else-if="!isOwner && storyList.length > 0"
          v-show="storyList.length > 0"
          v-model:selected-idx="selectedSorterIdx"
        >
          <div v-for="(item, idx) in SORTER_ENUM" :key="idx">
            {{ item.text }}
          </div>
        </boxed-tab>
      </div>
      <div class="flex gap-1">
        <button
          v-if="isOwner"
          class="btn rounded btn-xs"
          @click="showCreateGuide = true"
        >
          <plus-icon class="icon-sm" />
          {{ $t("Buttons.newChapter") }}
        </button>
      </div>
    </div>
    <div
      v-show="storyList.length === 0"
      class="h-40 flex flex-col items-center justify-center"
    >
      <div class="text-2xl font-bold text-base-content/50">
        {{ $t("Message.noContentAvailable") }}
      </div>
      <div class="text-base-content/50">
        {{ $t("Message.currentCreateStory") }}
      </div>
    </div>
    <div v-show="storyList.length > 0" class="flex flex-col gap-4">
      <story-chapter-item
        v-for="(item, idx) in filteredChapterList"
        :key="item.id"
        :next-item="
          (idx !== filteredChapterList.length - 1 &&
            filteredChapterList[idx + 1]) ||
          undefined
        "
        :pre-item="(idx !== 0 && filteredChapterList[idx - 1]) || undefined"
        :story="item"
        @onEditClick="
          currentStory = item;
          showEditPopup = true;
        "
        @onMediaPreview="onPreview"
        @onPublishClick="
          toPublishStory = item;
          showPublishPopup = true;
        "
        @onShareClick="onShareClick"
      />
      <button
        v-if="!showFullList && slicedStoryList.length !== storyList.length"
        class="btn btn-sm w-full"
        @click="onShowThreeMoreClick"
      >
        {{ $t("story.loadThreeMore") }}
        （{{ storyList.length - slicedStoryList.length }}）
      </button>
    </div>
  </div>
  <mate-media-preview
    v-model:show="showPreview"
    :mate-media-list="previewMediaList"
    :show-media-info="false"
    :start-position="startPosition"
  />
  <create-story-guide
    v-if="storyBook"
    v-model:show="showCreateGuide"
    :enable-duplicate="storyList.length > 0"
    :story-book="storyBook"
  />
  <!-- <story-share-popup
    v-if="storyBook"
    v-model:show="showSharePopup"
    :story="toSharePopup!"
  /> -->
  <screenshot-share-popup
    v-if="storyBook"
    ref="screenshotSharePopupRef"
    :on-screenshot-generate="() => storyStore.storyScreenshot(toSharePopup!.uuid)"
    :story="toSharePopup!"
  />
  <story-chapter-publish-popup
    v-if="storyBook"
    v-model:show="showPublishPopup"
    :story="toPublishStory!"
  />
  <VanPopup
    v-if="currentStory"
    v-model:show="showEditPopup"
    class="w-[350px] py-10"
    position="center"
    round
  >
    <story-info-edit
      v-if="showEditPopup"
      ref="storyInfoEditRef"
      :description="currentStory.description"
      :story="currentStory"
      :media="currentStory.media"
      :story-id="currentStory.id"
      :storyBookId="currentStory.storyBookId"
      :title="currentStory.title"
      :video-media="currentStory.videoMedia"
      enable-edit-video
      @on-success="onStoryEditSuccess"
      @on-cancel="showEditPopup = false"
    />
  </VanPopup>
</template>
